import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useOfferDiscountAPRDisplay } from '@/enterprise/features/takerMarketTable/utils/useOfferDiscountAPRDisplay';
import VariableRateDetailModal from '@/enterprise/features/variableRate/DetailModal';
import handleNaN from '@/utils/handleNaN';
import useLocaleFormat from '@/utils/useLocaleFormat';
import EmptyRender from '../../EmptyRender';
import { useOfferBuilderContext } from '../OfferBuilderContext';
import { calculateDiscountFromAmount } from '../rateHelpers';
import useDraftRateDetails from '../useDraftRateDetails';

const DraftTakerMarketRateDisplay = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const {
    state: { draftOfferProperties },
  } = useOfferBuilderContext();
  const [openRateModal, setOpenRateModal] = useState<boolean>(false);
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();
  const { t } = useTranslation();
  const { asCurrency, asPercent } = useLocaleFormat();
  const { getDraftRateDetails } = useDraftRateDetails();

  // Find the rate information for this specific taker market
  const rateInformation = draftOfferProperties?.rateInformations?.find(
    (ri) => ri.marketUuid === takerMarket.marketUuid && ri.supplierDivisionUuid === takerMarket.takerDivisionUuid
  )?.rateInformation;

  if (!rateInformation) {
    return '--';
  }

  // Handle both REF and benchmark APR rates with variable rate fields
  if (rateInformation.rateType === 'REF' || (rateInformation.rateType === 'APR' && rateInformation.rateName)) {
    const totalRate = rateInformation.rate ?? 0;
    const rateSourceName = rateInformation.rateName;
    const referenceRate = rateInformation.referenceRate ?? 0;
    const spread = rateInformation.spread ?? 0;

    const discountAmount = calculateDiscountFromAmount({
      dpeWeightedSum: takerMarket.eligibleDpeWeightedSum ?? 0,
      invoiceAmount: takerMarket.eligibleInvoiceAmount ?? 0,
      rate: totalRate,
    });
    const formattedDiscountAmount = asCurrency(handleNaN(0 - discountAmount), takerMarket.currency);
    const formattedEstimatedRate = `${asPercent({ value: handleNaN(totalRate / 100), decimals: 2 })} ${t(
      'core.aprAbbreviation'
    )}`;
    const formattedReferenceRate = asPercent({ value: handleNaN(referenceRate / 100), decimals: 2 });
    const formattedSpread = asPercent({ value: handleNaN(spread / 100), decimals: 2 });

    return (
      <EmptyRender condition={!!rateSourceName}>
        <VariableRateDetailModal
          formattedDiscountAmount={formattedDiscountAmount}
          formattedEstimatedRate={formattedEstimatedRate}
          formattedReferenceRate={formattedReferenceRate}
          formattedSpread={formattedSpread}
          onCancel={() => setOpenRateModal(false)}
          open={openRateModal}
          rateName={rateSourceName}
          showZeroApAlert={(takerMarket?.eligibleInvoiceAmount || 0) <= 0}
        />
        <div className="flex items-center gap-1">
          {formattedEstimatedRate}
          <button aria-label="Market Rate Info" onClick={() => setOpenRateModal(true)} type="button">
            <InfoCircleIcon className="h-4 w-4 fill-text-secondary" />
          </button>
        </div>
      </EmptyRender>
    );
  }

  const { rate, rateType } = getDraftRateDetails(takerMarket);

  const rateDisplay =
    rateType === 'DISC'
      ? offerDiscountAPRDisplay(rate ?? 0, t('core.discountAbbreviation'))
      : offerDiscountAPRDisplay(rate ?? 0, t('core.aprAbbreviation'));

  return (
    <EmptyRender condition={!!rateInformation.rate || !!rateInformation.rateType}>
      <div className="uppercase">{rateDisplay}</div>
    </EmptyRender>
  );
};

export default DraftTakerMarketRateDisplay;
